/**
 * Promotions Management Types
 * TypeScript interfaces for promotions and promotional offers
 */

import { ApiResponse, Pageable, PagedData } from './types';
import { CityMaster, VehicleCategory } from './pricingTypes';

// Promotion type based on PromotionsDTO from Swagger
export interface Promotion {
  promoCodeId: number;
  vehicleCategoryId: number;
  city: CityMaster;
  promoCode: string;
  description: string;
  discountType: string;
  discountValue: number;
  maxDiscountAmount: number;
  validFrom: string; // ISO date string
  validTo: string; // ISO date string
  applicableUserType: string;
  maxUsesPerUser: number;
  totalRedemptions: number;
  isActive: boolean;
  createdBy?: string;
  createdDate?: string;
  isDeletedValue?: boolean;
  modifiedBy?: string;
  modifiedDate?: string;
}

// DTO types for API requests
export type PromotionDTO = Omit<Promotion, 'promoCodeId' | 'createdDate' | 'modifiedDate'> & {
  promoCodeId?: number;
  city: CityMaster; // API expects complete CityMaster object, not just cityId
};

// Extended Promotion with vehicle category info for display
export interface PromotionWithDetails extends Promotion {
  vehicleCategory?: VehicleCategory;
}

// Query parameters for listing
export interface PromotionListQueryParams {
  page?: number;
  size?: number;
  sort?: string[];
  search?: string;
  vehicleCategoryId?: number;
  cityId?: number;
  isActive?: boolean;
  discountType?: string;
  applicableUserType?: string;
  validFrom?: string;
  validTo?: string;
}

// Status options
export type StatusOption = 'all' | 'active' | 'scheduled' | 'expired' | 'inactive';

// Discount type options
export type DiscountTypeOption = 'all' | 'PERCENTAGE' | 'FIXED_AMOUNT';

// User type options
export type UserTypeOption = 'all' | 'DRIVER' | 'PASSENGER' | 'ALL_USERS';

// Promotion statistics
export interface PromotionStats {
  totalPromotions: number;
  activePromotions: number;
  inactivePromotions: number;
  expiredPromotions: number;
  totalRedemptions: number;
  averageDiscountValue: number;
}

// Form validation schema
export interface PromotionForm {
  vehicleCategoryId: number;
  cityId: number;
  promoCode: string;
  description: string;
  discountType: string;
  discountValue: number;
  maxDiscountAmount: number;
  validFrom: string;
  validTo: string;
  applicableUserType: string;
  maxUsesPerUser: number;
  isActive: boolean;
}

// Promotion status based on dates
export type PromotionStatus = 'active' | 'scheduled' | 'expired' | 'inactive';

// Validation errors interface
export interface PromotionFormErrors {
  vehicleCategoryId?: string;
  cityId?: string;
  promoCode?: string;
  description?: string;
  discountType?: string;
  discountValue?: string;
  maxDiscountAmount?: string;
  validFrom?: string;
  validTo?: string;
  applicableUserType?: string;
  maxUsesPerUser?: string;
}

// Export constants for dropdown options
export const DISCOUNT_TYPES = [
  { value: 'PERCENTAGE', label: 'Percentage (%)' },
  { value: 'FIXED_AMOUNT', label: 'Fixed Amount ($)' }
];

export const USER_TYPES = [
  { value: 'DRIVER', label: 'Driver Only' },
  { value: 'PASSENGER', label: 'Passenger Only' },
  { value: 'ALL_USERS', label: 'All Users' }
];

export const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'expired', label: 'Expired' },
  { value: 'inactive', label: 'Inactive' }
];

// Re-export from pricing types for consistency
export type { ApiResponse, PagedData } from './types';